// --- Dependencies -------------------------------------------------------------------------------
import { MenuItem, TextField } from "@mui/material";
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePermission from "@/hooks/usePermission";
import useStore from "@/hooks/useStore";
import type { TyActiveStore } from "@/hooks/useStore/types";
import type { Lang } from "@/hooks/useLang/types";
import useStoreLanguages from "@/hooks/useStoreLanguages";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import OutlinedField from "@/UI/components/OutlinedField";
import SettingsCard from "@/UI/components/SettingsCard";
import MultiLangInput from "@/UI/inputs/MultiLangInput";
import ImagePicker from "@/UI/inputs/ImagePicker";
import { getMenuHost, getTextFieldSx, hasAnyLangValue, readLangs, withCacheBust, writeLangs } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type TyStoreType = "cafe" | "restaurant" | "both" | "other";

type TyForm = {
    name: TyLangValues;
    tagline: TyLangValues;
    about: TyLangValues;
    type: TyStoreType | "";
};

// --- Helpers ------------------------------------------------------------------------------------

const toForm = (store: TyActiveStore | null, languages: Lang[]): TyForm => ({
    name: readLangs(store?.name, languages),
    tagline: readLangs(store?.tagline, languages),
    about: readLangs(store?.about, languages),
    type: store?.type ?? "",
});

// --- Logic --------------------------------------------------------------------------------------

export default function SettingsBrand() {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const { languages, defaultLang } = useStoreLanguages();
    const { canEdit } = usePermission();
    const storeId = useStore((state) => state.storeId);
    const setStore = useStore((state) => state.setStore);

    const editable = canEdit("settings");
    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir);

    const { data: storeData } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store");
    const {
        data: saveData,
        loading: saving,
        error: saveError,
        invoke: save,
    } = useFetch<TyApiResponse<{ store: TyActiveStore }>>("/settings/store", { method: "PUT" }, false);

    // The section's own copy of the store: seeded by the fetch, then replaced by whatever a save
    // returns. Without this the form would keep diffing against the stale fetched values and stay
    // dirty forever after a successful save.
    const [store, setStoreData] = useState<TyActiveStore | null>(null);
    const [form, setForm] = useState<TyForm>(toForm(null, languages));
    const [logo, setLogo] = useState<File | null>(null);
    const [logoCleared, setLogoCleared] = useState(false);
    const [cover, setCover] = useState<File | null>(null);
    const [coverCleared, setCoverCleared] = useState(false);
    const [saved, setSaved] = useState(false);

    const reset = (source: TyActiveStore | null) => {
        setForm(toForm(source, languages));
        setLogo(null);
        setLogoCleared(false);
        setCover(null);
        setCoverCleared(false);
    };

    useEffect(() => {
        const fetched = storeData?.data?.store;
        if (fetched) setStoreData(fetched);
    }, [storeData]);

    useEffect(() => {
        if (store) reset(store);
    }, [store]);

    useEffect(() => {
        if (!saveData?.success) return;
        // Same S3 key on re-upload, so the logo needs a new version to escape the browser cache.
        const updated = {
            ...saveData.data.store,
            logo: withCacheBust(saveData.data.store.logo),
            coverPicture: withCacheBust(saveData.data.store.coverPicture),
        };
        // Becomes the section's new baseline, which clears the form and closes the actions bar.
        setStoreData(updated);
        // Keep the sidebar badge and every other store-aware surface in step with the new details.
        if (storeId) setStore(storeId, updated);
        setSaved(true);
        const timer = setTimeout(() => setSaved(false), 2600);
        return () => clearTimeout(timer);
    }, [saveData]);

    const initial = toForm(store, languages);
    const currentLogo = logoCleared ? null : (store?.logo ?? null);
    const currentCover = coverCleared ? null : (store?.coverPicture ?? null);
    // The list fields keep canonical order, so comparing them joined is enough to spot a real change.
    const fieldsChanged = (Object.keys(initial) as Array<keyof TyForm>).some((key) => JSON.stringify(form[key]) !== JSON.stringify(initial[key]));
    const dirty = Boolean(store) && (fieldsChanged || Boolean(logo) || logoCleared || Boolean(cover) || coverCleared);

    const isValid = hasAnyLangValue(form.name) && Boolean(form.type);

    const update = (patch: Partial<TyForm>) => setForm((prev) => ({ ...prev, ...patch }));

    const handleSave = () => {
        if (!dirty || !isValid) return;
        save({
            data: {
                name: writeLangs(form.name, languages, defaultLang),
                tagline: writeLangs(form.tagline, languages, defaultLang),
                about: writeLangs(form.about, languages, defaultLang),
                type: form.type,
                ...(logo ? { logo } : logoCleared ? { logo: null } : {}),
                ...(cover ? { coverPicture: cover } : coverCleared ? { coverPicture: null } : {}),
            },
        });
    };

    return (
        <SettingsCard
            icon="/icons/store-icon.svg"
            title={t.settings.brandTitle}
            subtitle={t.settings.brandSubtitle}
            badge={editable ? undefined : t.settings.readOnlyBadge}
            actions={
                editable
                    ? {
                          dirty,
                          saving,
                          saved,
                          error: saveError,
                          saveLabel: t.settings.saveButton,
                          savingLabel: t.settings.savingButton,
                          cancelLabel: t.settings.cancelButton,
                          savedLabel: t.settings.savedMessage,
                          onSave: handleSave,
                          onCancel: () => reset(store),
                      }
                    : undefined
            }
        >
            <div className="settings-field settings-field--full">
                <OutlinedField label={t.settings.logoLabel}>
                    <ImagePicker
                        url={currentLogo}
                        file={logo}
                        onPick={(file) => {
                            setLogo(file);
                            setLogoCleared(false);
                        }}
                        onRemove={() => {
                            setLogo(null);
                            setLogoCleared(true);
                        }}
                        changeLabel={t.settings.changePicture}
                        removeLabel={t.settings.removePicture}
                        placeholder="/icons/store-icon.svg"
                        shape="square"
                        disabled={!editable || saving}
                    />
                </OutlinedField>
            </div>

            <div className="settings-field settings-field--full">
                <MultiLangInput
                    label={t.settings.storeNameLabel}
                    languages={languages}
                    values={form.name}
                    onChange={(entry, value) => update({ name: { ...form.name, [entry]: value } })}
                    disabled={!editable || saving}
                />
            </div>

            <div className="settings-field settings-field--full">
                <OutlinedField label={t.settings.coverLabel}>
                    <ImagePicker
                        url={currentCover}
                        file={cover}
                        onPick={(file) => {
                            setCover(file);
                            setCoverCleared(false);
                        }}
                        onRemove={() => {
                            setCover(null);
                            setCoverCleared(true);
                        }}
                        changeLabel={t.settings.changePicture}
                        removeLabel={t.settings.removePicture}
                        placeholder="/icons/store-icon.svg"
                        shape="wide"
                        disabled={!editable || saving}
                    />
                </OutlinedField>
                <p className="settings-field-hint">{t.settings.coverHint}</p>
            </div>

            <div className="settings-field settings-field--full">
                <MultiLangInput
                    label={t.settings.taglineLabel}
                    languages={languages}
                    values={form.tagline}
                    onChange={(entry, value) => update({ tagline: { ...form.tagline, [entry]: value } })}
                    disabled={!editable || saving}
                />
                <p className="settings-field-hint">{t.settings.taglineHint}</p>
            </div>

            <div className="settings-field settings-field--full">
                {/* Multiline because the store's own line breaks are the only formatting it gets, and
                    the menu keeps them exactly as typed. */}
                <MultiLangInput
                    multiline
                    label={t.settings.aboutLabel}
                    languages={languages}
                    values={form.about}
                    onChange={(entry, value) => update({ about: { ...form.about, [entry]: value } })}
                    disabled={!editable || saving}
                />
                <p className="settings-field-hint">{t.settings.aboutHint}</p>
            </div>

            <div className="settings-field">
                {/* The slug is printed on QR codes that cannot be recalled, so it is not self-service. */}
                <TextField
                    fullWidth
                    disabled
                    label={t.settings.slugLabel}
                    value={store?.slug ?? ""}
                    slotProps={{ htmlInput: { dir: "ltr" } }}
                    sx={textFieldSx}
                />
                <p className="settings-field-hint">
                    {t.settings.slugHint}{" "}
                    <span className="settings-brand-slug-url" dir="ltr">
                        {getMenuHost(store?.slug || t.settings.slugPlaceholder)}
                    </span>
                    <br />
                    {t.settings.slugLockedHint}
                </p>
            </div>

            <div className="settings-field">
                <TextField
                    select
                    fullWidth
                    label={t.settings.typeLabel}
                    value={form.type}
                    onChange={(e) => update({ type: e.target.value as TyStoreType })}
                    disabled={!editable || saving}
                    sx={textFieldSx}
                >
                    <MenuItem value="cafe">{t.settings.typeCafe}</MenuItem>
                    <MenuItem value="restaurant">{t.settings.typeRestaurant}</MenuItem>
                    <MenuItem value="both">{t.settings.typeBoth}</MenuItem>
                    <MenuItem value="other">{t.settings.typeOther}</MenuItem>
                </TextField>
            </div>
        </SettingsCard>
    );
}
